import React, { memo, useCallback, useMemo, useState } from 'react'
import { Form, Formik } from 'formik'
import { useSelector } from 'react-redux'
import type { PaymentConfig, SaveVendorFeesPayload } from 'src/interfaces/payment'
import { getVendorFeesSaving } from 'src/selectors/paymentConfig'
import { ConfirmPricingModal } from '../ConfirmPricingModal'
import type { PricingChange } from '../pricingRecap'
import { VendorFeesFormContent } from './VendorFeesFormContent'
import { vendorFeesValidator } from './VendorFeesForm.validator'
import { toFormValues, toSavePayload } from './VendorFeesForm.mapper'
import { buildVendorFeesRecap } from './vendorFeesRecap'
import type { VendorFeesFormValues } from './VendorFeesForm.interface'

interface Props {
  config: PaymentConfig
  partnerId: string
  customerName: string
  isReadOnly: boolean
  /** Resolves true when the save succeeded — see the note on `handleConfirm`. */
  onSubmit(payload: SaveVendorFeesPayload): Promise<boolean>
}

const VendorFeesFormComponent = ({
  config,
  partnerId,
  customerName,
  isReadOnly,
  onSubmit,
}: Props) => {
  const isSaving = useSelector(getVendorFeesSaving)
  const [pending, setPending] = useState<VendorFeesFormValues | null>(null)

  const initialValues = useMemo(() => {
    return toFormValues(config)
  }, [config])

  // Formik 2.4.9's `submitForm` bails on a non-promise return and leaves `isSubmitting` true
  // forever, so this resolves explicitly instead of implicitly returning undefined.
  const handleSubmit = useCallback(async (values: VendorFeesFormValues) => {
    setPending(values)
  }, [])

  const handleClose = useCallback(() => {
    setPending(null)
  }, [])

  // Awaited, clearing `pending` only on success — identical reasoning to `PayoutSettingsForm`:
  // the dialog must outlive the request so its spinner is visible and a failure leaves it open.
  const handleConfirm = useCallback(async () => {
    if (pending === null) {
      return
    }

    const saved = await onSubmit(toSavePayload(partnerId, pending))

    if (!saved) {
      return
    }

    setPending(null)
  }, [pending, partnerId, onSubmit])

  const changes: PricingChange[] = useMemo(() => {
    if (pending === null) {
      return []
    }

    return buildVendorFeesRecap(initialValues.rows, pending.rows)
  }, [initialValues, pending])

  return (
    <Formik<VendorFeesFormValues>
      initialValues={initialValues}
      validationSchema={vendorFeesValidator}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      <Form>
        <VendorFeesFormContent isReadOnly={isReadOnly} />
        <ConfirmPricingModal
          isOpen={pending !== null}
          customerName={customerName}
          changes={changes}
          isSaving={isSaving}
          onConfirm={handleConfirm}
          onClose={handleClose}
        />
      </Form>
    </Formik>
  )
}

export const VendorFeesForm = memo(VendorFeesFormComponent)
